import { v4 } from 'uuid';
import websocketService from '../../../websocketService';
import {
  WEBSOCKET_EVENT_ADD_SUBSCRIBER,
  WEBSOCKET_EVENT_PUBLISH_MESSAGE,
  WEBSOCKET_EVENT_REMOVE_SUBSCRIBER,
} from '../mutations/mutationTypes';

const uuidv4 = v4;

export default {
  async UPDATE_WEB_SOCKET_CONNECTION_ID({ commit }, connectionId) {
    commit('SET_WEB_SOCKET_CONNECTION_ID', connectionId);
  },

  async UPDATE_WEB_SOCKET_STATUS({ commit }, status) {
    commit('SET_WEBSOCKET_STATUS', status);
  },

  SEND_WEBSOCKET_MESSAGE({ commit, state }, payload) {
    const DEBOUNCE_DELAY_DEFAULT = 1000; // Default debounce delay in ms
    const MAX_WAIT_TIME = 60000; // Max wait time for WebSocket connection in ms
    const INTERVAL_TIME = 200; // Interval for WebSocket connection polling in ms

    const { action, data, key } = payload;

    // Validate required fields
    if (!action) {
      console.error('SEND_WEBSOCKET_MESSAGE: Missing "action" in payload.');
      return;
    }

    if (!data) {
      console.error('SEND_WEBSOCKET_MESSAGE: Missing "data" in payload.');
      return;
    }

    const messageKey = key ? key + (data._id || data) : action;
    const debounceDelay = key ? DEBOUNCE_DELAY_DEFAULT : 0;

    console.log(
      `SEND_WEBSOCKET_MESSAGE: Processing action "${action}" with key "${messageKey}".`
    );

    // Clear existing debounce timer
    if (state.debounceTimers.has(messageKey)) {
      console.log(
        `SEND_WEBSOCKET_MESSAGE: Clearing existing timer for key "${messageKey}".`
      );
      clearTimeout(state.debounceTimers.get(messageKey));
    }

    // Debounced send logic
    const timer = setTimeout(async () => {
      console.log(
        `SEND_WEBSOCKET_MESSAGE: Timer expired for key "${messageKey}". Preparing to send.`
      );

      let elapsedTime = 0;

      // Wait for WebSocket connection if required
      const waitForConnection = async () => {
        return new Promise((resolve, reject) => {
          const interval = setInterval(() => {
            elapsedTime += INTERVAL_TIME;

            if (
              state.websocketStatus !== 'Disconnected' ||
              action === 'registerToken'
            ) {
              clearInterval(interval);
              resolve();
            } else if (elapsedTime >= MAX_WAIT_TIME) {
              clearInterval(interval);
              reject(new Error('WebSocket connection timeout.'));
            } else if (elapsedTime % 1000 === 0) {
              console.log(
                `Waiting for WebSocket connection... (${elapsedTime} ms elapsed)`
              );
            }
          }, INTERVAL_TIME);
        });
      };

      try {
        await waitForConnection();
        console.log(
          `SEND_WEBSOCKET_MESSAGE: Sending message for action "${action}".`
        );

        const fifoMessage = {
          MessageGroupId: 'default',
          MessageDeduplicationId: uuidv4(),
          MessageBody: payload,
        };

        websocketService.sendMessage(fifoMessage);
      } catch (error) {
        console.error(
          `SEND_WEBSOCKET_MESSAGE: Failed to send message for action "${action}".`,
          error
        );
      } finally {
        console.log(
          `SEND_WEBSOCKET_MESSAGE: Cleaning up timer for key "${messageKey}".`
        );
        commit('REMOVE_DEBOUNCE_TIMER', messageKey);
      }
    }, debounceDelay);

    console.log(
      `SEND_WEBSOCKET_MESSAGE: Setting new timer for key "${messageKey}" with delay ${debounceDelay} ms.`
    );
    commit('SET_DEBOUNCE_TIMER', { key: messageKey, timer });
  },

  WEBSOCKET_EVENT_SUBSCRIBE_TO_ACTION({ commit }, { action, callback }) {
    commit(
      WEBSOCKET_EVENT_ADD_SUBSCRIBER,
      { action, callback },
      { root: true }
    );
  },

  WEBSOCKET_EVENT_UNSUBSCRIBE_FROM_ACTION({ commit }, { action, callback }) {
    commit(
      WEBSOCKET_EVENT_REMOVE_SUBSCRIBER,
      { action, callback },
      { root: true }
    );
  },

  WEBSOCKET_EVENT_HANDLE_MESSAGE({ commit }, message) {
    try {
      const { action, data } = message;

      // Publish message to all subscribers of this action
      commit(WEBSOCKET_EVENT_PUBLISH_MESSAGE, { action, data }, { root: true });
    } catch (error) {
      console.error('Failed to handle WebSocket message:', error);
    }
  },
};
